import { createHash, createHmac, randomBytes, timingSafeEqual } from 'crypto';

export const MarketingSubscriptionTokenPurpose = {
  CONFIRM: 'confirm',
  UNSUBSCRIBE: 'unsubscribe',
  PREFS: 'prefs',
} as const;

export type MarketingSubscriptionTokenPurposeValue =
  (typeof MarketingSubscriptionTokenPurpose)[keyof typeof MarketingSubscriptionTokenPurpose];

export function hashMarketingToken(rawToken: string): string {
  return createHash('sha256').update(rawToken.trim()).digest('hex');
}

export function mintMarketingSubscriptionToken(): { raw: string; hash: string } {
  const raw = randomBytes(32).toString('base64url');
  return { raw, hash: hashMarketingToken(raw) };
}

/** Signed one-click payload for List-Unsubscribe-Post (stateless fallback). */
export function signMarketingUnsubPayload(
  input: { publicationId: string; email: string; exp: number },
  secret: string,
): string {
  const payload = Buffer.from(
    JSON.stringify({
      p: input.publicationId,
      e: input.email.trim().toLowerCase(),
      exp: input.exp,
    }),
  ).toString('base64url');
  const sig = createHmac('sha256', secret).update(payload).digest('base64url');
  return `${payload}.${sig}`;
}

export function verifyMarketingUnsubPayload(
  token: string,
  secret: string,
): { publicationId: string; email: string } | null {
  const [payload, sig] = token.split('.');
  if (!payload || !sig) return null;
  const expected = createHmac('sha256', secret).update(payload).digest('base64url');
  const a = Buffer.from(sig);
  const b = Buffer.from(expected);
  if (a.length !== b.length || !timingSafeEqual(a, b)) return null;
  try {
    const parsed = JSON.parse(Buffer.from(payload, 'base64url').toString('utf8')) as {
      p?: string;
      e?: string;
      exp?: number;
    };
    if (!parsed.p || !parsed.e || !parsed.exp) return null;
    if (Date.now() > parsed.exp) return null;
    return { publicationId: parsed.p, email: parsed.e };
  } catch {
    return null;
  }
}

export function marketingConfirmUrl(webBaseUrl: string, rawToken: string) {
  return `${webBaseUrl.replace(/\/$/, '')}/n/confirm?token=${encodeURIComponent(rawToken)}`;
}

export function marketingUnsubscribeUrl(webBaseUrl: string, rawToken: string) {
  return `${webBaseUrl.replace(/\/$/, '')}/account/email-preferences?token=${encodeURIComponent(rawToken)}`;
}

export function marketingOneClickUnsubUrl(webBaseUrl: string, signedPayload: string) {
  return `${webBaseUrl.replace(/\/$/, '')}/api/marketing/public/unsubscribe/one-click?t=${encodeURIComponent(signedPayload)}`;
}
